import {AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy} from '@angular/core';
import wikipediaPreview from 'wikipedia-preview';

/**
 * Wraps `wikipedia-preview` on the host element and fixes NP20-727:
 * - closes the (shared, body-level) popup on scroll and when the pointer leaves a
 *   skill chip (delegated `mouseout` on the host — the library binds its own per-chip
 *   mouseleave only inside the async, fetch-driven show, so it races and misses),
 * - closes it right after it appears if the pointer already left before that async
 *   show resolved (otherwise the popup pops up late and just stays), and
 * - tears everything down on destroy.
 *
 * The library exposes no close/destroy API, so the only clean way to dismiss the
 * popup — resetting its internal `currentTargetElement` so re-hovering re-opens it —
 * is to click its own close button (`.wikipediapreview-header-closebtn`).
 *
 * `init()` runs outside the Angular zone: the hover/leave listeners and the internal
 * 300ms auto-hide timer the library registers must not trigger change detection.
 * In-zone init made every hover fire CD and, on the large project editor (one init
 * per task), caused multi-100ms CD passes that starved the auto-hide — so the popup
 * stopped closing on mouseleave.
 */
@Directive({
  selector: '[wikiPreview]',
  standalone: true,
})
export class WikiPreviewDirective implements AfterViewInit, OnDestroy {
  @Input() wikiPreviewLang = 'de';
  @Input() wikiPreviewSelector = '.wiki';
  @Input() wikiPreviewPopupContainer?: HTMLElement | null;

  private readonly onScroll = (): void => this.closeOpenPopup();
  private readonly onHostMouseOut = (event: MouseEvent): void => {
    // Delegated on the host: react only when the pointer actually leaves a skill chip,
    // not the whole card. `mouseout` bubbles (mouseleave does not), so one listener
    // covers every chip and survives chip re-renders.
    const wiki = (event.target as Element | null)?.closest?.('.wiki');
    if (!wiki) {
      return; // the pointer did not leave a skill chip
    }
    const to = event.relatedTarget as Element | null;
    if (to && wiki.contains(to)) {
      return; // still inside the same chip (moving across its inner nodes)
    }
    if (to?.closest?.('.wp-popup')) {
      return; // moving into the popup to read/click through it
    }
    this.closeOpenPopup();
  };

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    // Everything here only touches the library's own DOM / native listeners, never
    // Angular state, so it runs outside the zone (see class doc). `.wiki` targets are
    // already in the DOM by ngAfterViewInit, so no deferring setTimeout is needed.
    this.zone.runOutsideAngular(() => {
      wikipediaPreview.init({
        root: this.host.nativeElement,
        selector: this.wikiPreviewSelector,
        popupContainer: this.wikiPreviewPopupContainer ?? undefined,
        lang: this.wikiPreviewLang,
        prefersColorScheme: 'light',
        events: {
          // Fires each time a popup is shown, including after the (arbitrarily delayed)
          // network fetch. If the pointer already left before it appeared, no mouseleave
          // will follow — so close it on the next frame unless the pointer is still over
          // the host or the popup. This is the fix for the intermittent "stays open".
          onShow: (): void => {
            requestAnimationFrame(() => this.closeUnlessHovered());
          },
        },
      });

      // Close the popup on any scroll; capture phase catches inner scroll containers
      // too, since scroll events do not bubble.
      window.addEventListener('scroll', this.onScroll, {capture: true, passive: true});
      // ...and when the pointer leaves a skill chip (see class doc).
      this.host.nativeElement.addEventListener('mouseout', this.onHostMouseOut);
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onScroll, {capture: true});
    this.host.nativeElement.removeEventListener('mouseout', this.onHostMouseOut);
    this.closeOpenPopup();
  }

  private closeUnlessHovered(): void {
    const popup = document.querySelector<HTMLElement>('.wp-popup');
    if (this.host.nativeElement.matches(':hover') || popup?.matches(':hover')) {
      return;
    }
    this.closeOpenPopup();
  }

  private closeOpenPopup(): void {
    const popup = document.querySelector<HTMLElement>('.wp-popup');
    if (popup?.style.visibility === 'visible') {
      popup.querySelector<HTMLButtonElement>('.wikipediapreview-header-closebtn')?.click();
    }
  }
}
